import * as React from "react";
import { Link, useI18next, useTranslation } from "gatsby-plugin-react-i18next";

const languageLabel: Record<string, string> = {
  en: "EN",
  ar: "AR",
  tr: "TR",
  vi: "VI",
  sw: "SW",
  ha: "HA",
  fr: "FR",
  pt: "PT",
  es: "ES",
  so: "SO",
  bs: "BS",
  hi: "HI",
  ur: "UR",
  pa: "PA",
  id: "ID",
  bn: "BN",
  uz: "UZ",
  kk: "KK",
  tg: "TG",
  tk: "TK",
  az: "AZ",
  ky: "KY",
  am: "AM",
  ms: "MS",
  ps: "PS",
  sq: "SQ",
};

export default function LanguageSwitcher() {
  const { language, languages, originalPath } = useI18next();
  const { t } = useTranslation();
  const [open, setOpen] = React.useState(false);
  const rootRef = React.useRef<HTMLDivElement | null>(null);

  React.useEffect(() => {
    if (!open) return;
    const onPointerDown = (event: PointerEvent) => {
      const target = event.target as Node | null;
      if (!target) return;
      if (!rootRef.current?.contains(target)) setOpen(false);
    };
    window.addEventListener("pointerdown", onPointerDown);
    return () => window.removeEventListener("pointerdown", onPointerDown);
  }, [open]);

  return (
    <div ref={rootRef} className="language-switcher" aria-label={t("a11y.languageSwitcher")}>
      <div className="language-switcher-mobile">
        <button
          type="button"
          className="pill language-switcher-button"
          aria-expanded={open}
          aria-haspopup="menu"
          onClick={() => setOpen((prev) => !prev)}
          onKeyDown={(event) => {
            if (event.key === "Escape") setOpen(false);
          }}
        >
          {languageLabel[language] ?? language.toUpperCase()}
          <span aria-hidden="true" className="language-switcher-caret">
            ▾
          </span>
        </button>
        {open ? (
          <div className="language-switcher-menu" role="menu">
            {[...languages]
              .sort((a, b) => a.localeCompare(b))
              .map((lng) => (
              <Link
                key={lng}
                to={originalPath}
                language={lng}
                role="menuitem"
                aria-current={lng === language ? "page" : undefined}
                className="language-switcher-menu-item"
                onClick={() => setOpen(false)}
              >
                <span style={{ fontWeight: 900 }}>
                  {languageLabel[lng] ?? lng.toUpperCase()}
                </span>
                <span style={{ color: "var(--color-muted)" }}>{t(`languages.${lng}`)}</span>
              </Link>
            ))}
          </div>
        ) : null}
      </div>
    </div>
  );
}
